import { ContentAnalysis, InitialAssessment } from '@/services/noteGoApi/noteGoApiSchemas';

/**
 * Extracts the initial_assessment from a ContentAnalysis object, handling the
 * double-nested structure that sometimes occurs due to a backend bug.
 *
 * **Workaround Context:**
 * The backend sometimes returns a double-nested content_analysis structure where
 * the response contains { content_analysis: { content_analysis: { initial_assessment: ... } } }
 * instead of the expected { content_analysis: { initial_assessment: ... } }.
 *
 * This function checks for both the nested and non-nested structures to ensure
 * compatibility until the backend issue is resolved. Once the backend is fixed,
 * the nested structure handling can be removed.
 *
 * @param contentAnalysis - The ContentAnalysis object from the API response
 * @returns The InitialAssessment object if found, otherwise undefined
 */
export const getInitialAssessmentFromContentAnalysis = (
  contentAnalysis: ContentAnalysis | null | undefined,
): InitialAssessment | undefined => {
  if (!contentAnalysis) return undefined;

  const raw = contentAnalysis as Record<string, unknown>;

  // Check for double-nested structure (backend bug workaround)
  const nested = raw.content_analysis as ContentAnalysis | undefined;
  if (nested?.initial_assessment) {
    return nested.initial_assessment;
  }

  // Normal nested structure: { initial_assessment: { language, ... } }
  if (contentAnalysis.initial_assessment) {
    return contentAnalysis.initial_assessment;
  }

  // Flat structure: { language, content_type, ... } stored directly on content_analysis
  if (raw.language || raw.content_type || raw.technical_level) {
    return raw as unknown as InitialAssessment;
  }

  return undefined;
};
